
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Filter, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { OrganizationDialog } from "./OrganizationDialog";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { toast } from "sonner";

export const Organizations = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [selectedOrg, setSelectedOrg] = useState<{id: string; name: string} | null>(null);
  const queryClient = useQueryClient();

  const { data: regions } = useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regions")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  const { data: organizations, isLoading } = useQuery({
    queryKey: ["organizations", selectedRegion],
    queryFn: async () => {
      let query = supabase
        .from("organizations")
        .select(`
          *,
          region:regions(id, name)
        `)
        .order('name');
      
      if (selectedRegion !== "all") {
        query = query.eq('region_id', selectedRegion);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  const handleEdit = (org: {id: string; name: string}) => {
    setSelectedOrg(org);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedOrg) return;

    try {
      const { error } = await supabase
        .from("organizations")
        .delete()
        .eq("id", selectedOrg.id);

      if (error) throw error;

      toast.success("Organization deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setIsDeleteDialogOpen(false);
      setSelectedOrg(null);
    } catch (error) {
      console.error("Error deleting organization:", error);
      toast.error("Failed to delete organization");
    }
  };

  const confirmDelete = (org: {id: string; name: string}) => {
    setSelectedOrg(org);
    setIsDeleteDialogOpen(true);
  };

  if (isLoading) {
    return <div>Loading organizations...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {regions?.map((region) => (
                <SelectItem key={region.id} value={region.id}>
                  {region.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => {
          setSelectedOrg(null);
          setIsDialogOpen(true);
        }}>
          <Plus className="mr-2 h-4 w-4" />
          Add Organization
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {organizations?.map((org) => (
          <div key={org.id} className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{org.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Region: {org.region?.name}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleEdit(org)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => confirmDelete(org)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <OrganizationDialog 
        open={isDialogOpen} 
        onOpenChange={setIsDialogOpen}
        orgToEdit={selectedOrg}
      />

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Organization"
        description={`Are you sure you want to delete ${selectedOrg?.name}? This action will also delete all dependent sites. This action cannot be undone.`}
      />
    </div>
  );
};
